import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import { useCreateTypo, useUpdateTypo } from "@/query/typo";
import { editingTypoAtom } from "@/atom/editing";
import { useAgentId } from "@/hook";
import Button from "@/component/Button";
import { errorToast, successToast } from "@/component/Toast";
import { MultiTextInputGroup, MultiTextInputGroupRef, TextInputGroup } from "@/component/input";


const TypoForm: React.FC = () => {
    const agentId = useAgentId();
    const intl = useIntl();

    const typosInputRef = useRef<MultiTextInputGroupRef>(null);
    const [typoKeyword, setTypoKeyword] = useState<string>("");
    const [typos, setTypos] = useState<string[]>([]);
    const [validation, setValidation] = useState<{
        keyword: boolean,
        typos: boolean
    }>({ keyword: true, typos: true });

    const [editingTypo, setEditingTypo] = useAtom(editingTypoAtom);
    const { mutateAsync: createTypo, isPending: isCreating } = useCreateTypo(agentId);
    const { mutateAsync: updateTypo, isPending: isUpdating } = useUpdateTypo(agentId, editingTypo?.id);

    const clear = useCallback(() => {
        setEditingTypo(undefined);
        typosInputRef.current?.clear();
        setValidation({ keyword: true, typos: true });
        setTypoKeyword("");
        setTypos([]);
    }, [setEditingTypo, setValidation, setTypoKeyword, setTypos, typosInputRef]);

    const validate = () => {
        const currentValidation = {
            keyword: !!typoKeyword && typoKeyword.trim().length > 0,
            typos: typos.length > 0
        };
        setValidation(currentValidation);
        return Object.values(currentValidation).every(value => value);
    };

    const onSubmit = async () => {
        if (!validate()) {
            return;
        }
        const form = {
            agent_id: agentId,
            typo_id: "id",
            keyword: typoKeyword.trim(),
            typos: typos
        };

        if (editingTypo) {
            await updateTypo(form)
                .then(() => {
                    successToast(intl.formatMessage({ id: "t003" }));
                    clear();
                })
                .catch(() => {
                    errorToast(intl.formatMessage({ id: "t004" }));
                });
        } else {
            await createTypo(form)
                .then(() => {
                    successToast(intl.formatMessage({ id: "t001" }));
                    clear();
                })
                .catch(() => {
                    errorToast(intl.formatMessage({ id: "t002" }));
                });
        }
    };

    useEffect(() => {
        return () => {
            clear();
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (editingTypo) {
            setTypoKeyword(editingTypo.keyword);
            setTypos(editingTypo.typos);
        } else {
            clear();
        }
    }, [editingTypo, clear]);

    return (
        <div role="form" className="form-container w-left-editor-section flex-none">
            <div className="form-container-input-group">
                <TextInputGroup rowGap={8}
                                value={typoKeyword}
                                label={intl.formatMessage({ id: "i07002" })}
                                placeholder={intl.formatMessage({ id: "i07004" })}
                                invalid={!validation.keyword && !typoKeyword.trim()}
                                invalidText={intl.formatMessage({ id: "i07005" })}
                                onChange={setTypoKeyword}
                />

                <MultiTextInputGroup ref={typosInputRef}
                                     value={typos}
                                     label={intl.formatMessage({ id: "i07003" })}
                                     placeholder={intl.formatMessage({ id: "i07006" })}
                                     invalid={!validation.typos && !typos.length}
                                     invalidText={intl.formatMessage({ id: "i07007" })}
                                     onChange={setTypos}
                />
            </div>

            <div className="flex gap-2">
                {editingTypo && (
                    <Button variant="secondary"
                            size="full"
                            disabled={isUpdating || isCreating}
                            onClick={clear}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                )}
                <Button variant="primary"
                        size="full"
                        disabled={isUpdating || isCreating}
                        onClick={onSubmit}
                >
                    {intl.formatMessage({ id: editingTypo ? "b0001" : "b0010" })}
                </Button>
            </div>
        </div>
    );
};

export default TypoForm;